import React, { FC, useState, useEffect } from "react";
import styled from "styled-components";
import { CheckIconSvg } from "../../icons/CheckIconSvg";
import { convertFromCentsToDollars } from "../../../helpers/currency";
import { colors } from "../../../styles/variables";

const Wrapper = styled.div`
  width: 100%;
  padding-bottom: 30px;
`;

const Text = styled.div`
  font-weight: 500;
`;

const ProgressBarBackground = styled.div`
  height: 8px;
  width: 100%;
  border-radius: 2px;
  background-color: ${({ theme }) =>
    theme.cart?.freeShipSection?.progressBarBackgroundColor ||
    colors.borderGray};
`;

const StyledProgressBar = styled.div<{ percent: number }>`
  height: 8px;
  width: ${(props) => props.percent + "%"};
  border-radius: ${(props) => (props.percent === 100 ? "2px" : "2px 0 0 2px")};
  background-color: ${({ theme }) =>
    theme.cart?.freeShipSection?.progressBarColor || theme.color.primary};
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;

  > ${Text} {
    padding-left: 6px;
  }
`;

interface ProgressBarProps {
  subtotal: number;
  freeShippingThreshold: number;
}

export const getPercentInTenths = (
  freeShippingThreshold: number,
  subtotal: number
) => {
  return Math.floor((subtotal / freeShippingThreshold) * 10) * 10;
};

export const ProgressBar: FC<ProgressBarProps> = ({
  subtotal,
  freeShippingThreshold,
}) => {
  const [remainingForFreeShip, setRemainingForFreeShip] = useState(0);
  const [percentInTenths, setPercentInTenths] = useState(100);

  useEffect(() => {
    if (subtotal > freeShippingThreshold) {
      setRemainingForFreeShip(0);
      setPercentInTenths(100);
    } else {
      setRemainingForFreeShip(freeShippingThreshold - subtotal);
      setPercentInTenths(getPercentInTenths(freeShippingThreshold, subtotal));
    }
  }, [subtotal, freeShippingThreshold]);

  if (remainingForFreeShip) {
    return (
      <Wrapper data-testid="progress-bar">
        <TextWrapper>
          <Text>
            Add {convertFromCentsToDollars(remainingForFreeShip)} more to
            receive free shipping!
          </Text>
        </TextWrapper>
        <ProgressBarBackground>
          <StyledProgressBar
            percent={percentInTenths}
            data-testid="progress-bar-remaining"
          />
        </ProgressBarBackground>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="progress-bar">
      <TextWrapper>
        <CheckIconSvg color={colors.darkGray} />
        <Text>This order ships free!</Text>
      </TextWrapper>
      <StyledProgressBar percent={100} data-testid="progress-bar-free" />
    </Wrapper>
  );
};
